import { useState } from 'react'
import {
  Camera, CameraOff, CheckCircle2, Circle, Eye, Loader2, Mic, Radio, RefreshCw, ShieldAlert,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { preflight } from './livekit'
import {
  goLive, openCamera, stop, useAttachPreview, useBroadcastSession, useElapsed,
} from './useBroadcast'
import { accuracyNote, captureLocation, type LocationOutcome } from '@/shared/geo/geolocation'
import { fmtClock } from './players'

/**
 * Put this device on air.
 *
 * The order of the controls is the order of the decisions: open the camera, look
 * at yourself, then go live. Merging the two into one button would mean the first
 * time anybody sees the framing is after the audience does.
 */
export function BroadcastPanel({
  feedId, feedTitle, eventTitle, onEnded, compact,
}: {
  feedId: string
  feedTitle: string
  eventTitle?: string | null
  onEnded?: () => void
  compact?: boolean
}) {
  const [audioOnly, setAudioOnly] = useState(false)
  const [facing, setFacing] = useState<'environment' | 'user'>('environment')
  const [check] = useState(() => preflight())
  /**
   * Where the camera is, captured when Go live is pressed.
   *
   * Not on mount: a permission prompt the moment somebody opens the page, before
   * they have decided to broadcast, is the prompt people dismiss reflexively —
   * and a dismissed prompt is a `PERMISSION_DENIED` that sticks for the origin.
   * Asking at the moment it is needed is the ask people say yes to.
   */
  const [locating, setLocating] = useState(false)
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null)
  /**
   * A location read taken by "Check it now", before going live.
   *
   * Worth its own state because the failure is otherwise invisible: a browser
   * that has already denied the permission shows NO prompt and resolves straight
   * to `PERMISSION_DENIED`, so somebody watching for a dialog concludes the app
   * never asked. This lets them see the answer before it matters.
   */
  const [probe, setProbe] = useState<LocationOutcome | null>(null)

  const session = useBroadcastSession()
  const elapsed = useElapsed(session.startedAt)
  useAttachPreview(videoEl, session.phase)

  /**
   * This panel reflects the session only when the session is about THIS camera.
   *
   * The session is global now, so opening the drawer for a different camera
   * while one is on air must not paint that broadcast's ON AIR badge over the
   * wrong feed. A panel whose camera is not the live one shows an idle panel,
   * and the global bar keeps the real broadcast visible.
   */
  const mine = session.feedId === feedId
  const phase = mine ? session.phase : 'idle'
  const error = mine ? session.error : null
  const viewers = mine ? session.viewers : 0
  const deviceLabel = mine ? session.deviceLabel : null
  const place: LocationOutcome | null = (mine ? session.location : null) ?? probe
  const stoppedCleanly = mine ? session.stoppedCleanly : false
  const live = phase === 'live'
  /** Another camera is on air from this tab; one device, one camera. */
  const busyElsewhere = !mine
    && (session.phase === 'live' || session.phase === 'connecting'
      || session.phase === 'authorising' || session.phase === 'preview')

  return (
    <div className="space-y-3">
      {!check.ok && check.reason && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="space-y-1">
              <p className="text-erp-sm font-medium">This page cannot open a camera here</p>
              <p className="text-erp-xs text-muted-foreground">{check.reason}</p>
              {check.fix && <p className="text-erp-xs text-muted-foreground">{check.fix}</p>}
            </div>
          </div>
        </div>
      )}

      <div className="relative aspect-video overflow-hidden rounded-md bg-black">
        <video ref={setVideoEl} autoPlay muted playsInline className="h-full w-full object-contain" />

        {phase === 'idle' && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <Camera className="mx-auto h-6 w-6 text-white/60" />
              <p className="mt-1 text-erp-sm text-white/80">Camera is off</p>
            </div>
          </div>
        )}

        {(phase === 'authorising' || phase === 'connecting') && (
          <div className="absolute inset-0 grid place-items-center bg-black/60">
            <div className="text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-white/80" />
              <p className="mt-1 text-erp-sm text-white">
                {phase === 'authorising' ? 'Checking that you may broadcast…' : 'Going live…'}
              </p>
            </div>
          </div>
        )}

        {live && (
          <>
            <span className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-red-600 px-2 py-0.5 text-erp-xs font-semibold text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> ON AIR
            </span>
            <span className="absolute right-2 top-2 flex items-center gap-2 rounded-full bg-black/60 px-2 py-0.5 text-erp-xs text-white">
              <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {fmtNumber(viewers)}</span>
              <span className="tabular-nums">{fmtClock(elapsed)}</span>
            </span>
          </>
        )}
      </div>

      {!compact && (
        <div>
          <div className="text-erp-sm font-semibold">{feedTitle}</div>
          {eventTitle && <div className="text-erp-xs text-muted-foreground">{eventTitle}</div>}
        </div>
      )}

      <ErrorStrip message={error} />

      <div className="flex flex-wrap items-center gap-2">
        {(phase === 'idle' || phase === 'error') && (
          <>
            <button
              type="button"
              className="erp-btn erp-btn-primary h-11"
              disabled={!check.ok || busyElsewhere}
              onClick={() => void openCamera(
                { feedId, feedTitle, eventTitle }, { audioOnly, facingMode: facing },
              )}
            >
              <Camera className="h-3.5 w-3.5" /> Open camera
            </button>
            <label className="flex items-center gap-1.5 text-erp-sm">
              <input
                type="checkbox"
                checked={audioOnly}
                onChange={(e) => setAudioOnly(e.target.checked)}
              />
              <Mic className="h-3.5 w-3.5" /> Audio only
            </label>
            {busyElsewhere && (
              <span className="text-erp-xs text-amber-700 dark:text-amber-400">
                {session.feedTitle} is using this device's camera. Stop it first.
              </span>
            )}
          </>
        )}

        {phase === 'preview' && (
          <>
            <button
              type="button"
              className="erp-btn h-11 border-red-600 bg-red-600 font-semibold text-white hover:bg-red-700"
              disabled={locating}
              onClick={async () => {
                setLocating(true)
                // Awaited, not fired-and-forgotten: the coordinate has to be in
                // the token request, and a broadcast that started before the fix
                // arrived would record no location for its first minutes.
                const got = await captureLocation()
                setLocating(false)
                await goLive(got)
              }}
            >
              {locating
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding your location…</>
                : <><Radio className="h-3.5 w-3.5" /> Go live</>}
            </button>
            {!audioOnly && (
              <button
                type="button"
                className="erp-btn h-11"
                onClick={() => {
                  const next = facing === 'environment' ? 'user' : 'environment'
                  setFacing(next)
                  void openCamera({ feedId, feedTitle, eventTitle }, { audioOnly, facingMode: next })
                }}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Flip
              </button>
            )}
          </>
        )}

        {(live || phase === 'stopping') && (
          <button
            type="button"
            className="erp-btn h-11 border-red-600 text-red-600 hover:bg-red-600/10"
            disabled={phase === 'stopping'}
            onClick={async () => { await stop(); onEnded?.() }}
          >
            {phase === 'stopping'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <CameraOff className="h-3.5 w-3.5" />}
            Stop broadcast
          </button>
        )}
      </div>

      {/*
        Shown BEFORE the camera opens, not only in preview. Somebody should know
        their position will be recorded while they can still walk away from it —
        telling them once the camera is already on is late, and this is the kind
        of notice that is only worth anything early.
      */}
      {(phase === 'idle' || phase === 'preview' || phase === 'error') && (
        <Note tone="info">
          Pressing Go live records where this device is, so the footage can be placed on
          the coverage map and matched to the event later. If your browser refuses, the
          broadcast still starts and the refusal is recorded instead of a location.
        </Note>
      )}

      {(phase === 'idle' || phase === 'preview' || phase === 'error' || live) && (
        <>
          <dl className="text-erp-xs text-muted-foreground">
            <div className="flex gap-2">
              <dt className="font-medium">Device</dt>
              <dd className="truncate">{deviceLabel || '—'}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium">Location</dt>
              <dd className="min-w-0 flex-1">
                {!place ? (
                  <span className="text-muted-foreground">
                    Read when you press Go live.
                    {' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={async () => {
                        setLocating(true)
                        setProbe(await captureLocation())
                        setLocating(false)
                      }}
                    >
                      Check it now
                    </button>
                  </span>
                ) : place.ok ? (
                  <>
                    {place.location.latitude.toFixed(5)}, {place.location.longitude.toFixed(5)}
                    {accuracyNote(place.location.accuracyM) && (
                      <span className="block text-muted-foreground/80">
                        {accuracyNote(place.location.accuracyM)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-amber-700 dark:text-amber-400">
                    Not recorded — {place.reason}
                  </span>
                )}
              </dd>
            </div>
          </dl>
          {live && (
            <p className="flex items-start gap-1.5 text-erp-xs text-muted-foreground">
              <Circle className="mt-0.5 h-2.5 w-2.5 shrink-0 fill-red-600 text-red-600" />
              <span>
                Being recorded by the media server as you broadcast — saved whether or not
                anybody is watching. You can move around the app and stay on air; the ON AIR
                bar at the top follows you. <strong>Closing this tab ends the broadcast</strong>,
                because this device is the camera.
              </span>
            </p>
          )}
        </>
      )}

      {/*
        `stoppedCleanly`, not `phase === 'idle'`. A connection that was cut —
        force-ended by an administrator, token expired — also lands on `idle`, and
        congratulating somebody on a clean finish for a broadcast that was taken
        off air is a claim with nothing behind it.
      */}
      {stoppedCleanly && phase === 'idle' && (
        <div className="flex items-start gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
          <p className="text-erp-xs">
            Broadcast ended. The recording appears in the archive once the media server has
            written it — usually within a minute.
          </p>
        </div>
      )}

      {!check.ok && (
        <Note tone="info">
          You can still open this event on a laptop and broadcast from its webcam, which is
          always a secure context.
        </Note>
      )}

      <p className={cn('text-erp-xs text-muted-foreground', compact && 'sr-only')}>
        Video goes from this device to the media server and out to viewers. It does not pass
        through the Axis API.
      </p>
    </div>
  )
}
